import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { useAdminAuth } from "./AdminAuthContext";
import BgLogo from "../components/BgLogo";
import {
  LogOut, RefreshCw, Mail, Search, Trash2, CheckCircle2, Loader2, ExternalLink,
} from "lucide-react";

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function AdminMessagesPage() {
  const { admin, loading, authedFetch, logout } = useAdminAuth();
  const [messages, setMessages] = useState([]);
  const [total, setTotal] = useState(0);
  const [fetching, setFetching] = useState(true);
  const [err, setErr] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [query, setQuery] = useState("");

  const load = useCallback(async () => {
    setFetching(true);
    setErr("");
    try {
      const { data } = await authedFetch("/admin/contact-messages?limit=500");
      setMessages(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load messages");
    } finally {
      setFetching(false);
    }
  }, [authedFetch]);

  useEffect(() => {
    if (!admin) return;
    let cancelled = false;
    (async () => {
      setFetching(true);
      setErr("");
      try {
        const { data } = await authedFetch("/admin/contact-messages?limit=500");
        if (cancelled) return;
        setMessages(data.items || []);
        setTotal(data.total || 0);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || "Failed to load messages");
      } finally {
        if (!cancelled) setFetching(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [admin, authedFetch]);

  const markRead = useCallback(async (id) => {
    try {
      await authedFetch(`/admin/contact-messages/${id}/read`, { method: "POST" });
      setMessages((arr) => arr.map((m) => (m.id === id ? { ...m, read: true } : m)));
    } catch (e) {
      console.error(e);
    }
  }, [authedFetch]);

  const remove = async (id) => {
    if (!window.confirm("Delete this message permanently?")) return;
    try {
      await authedFetch(`/admin/contact-messages/${id}`, { method: "DELETE" });
      setMessages((arr) => arr.filter((m) => m.id !== id));
      setTotal((t) => Math.max(0, t - 1));
      if (selectedId === id) setSelectedId(null);
    } catch (e) {
      console.error(e);
    }
  };

  const filtered = useMemo(() => {
    if (!query.trim()) return messages;
    const q = query.toLowerCase();
    return messages.filter(
      (m) =>
        m.name?.toLowerCase().includes(q) ||
        m.email?.toLowerCase().includes(q) ||
        m.company?.toLowerCase().includes(q) ||
        m.subject?.toLowerCase().includes(q) ||
        m.message?.toLowerCase().includes(q)
    );
  }, [messages, query]);

  const selected = useMemo(() => messages.find((m) => m.id === selectedId), [messages, selectedId]);

  if (loading) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-brand-bg" data-testid="admin-loading">
        <Loader2 className="h-6 w-6 animate-spin text-brand-red" />
      </main>
    );
  }
  if (!admin) return <Navigate to="/admin/login" replace />;

  const unread = messages.filter((m) => !m.read).length;

  return (
    <main className="min-h-screen bg-brand-bg" data-testid="page-admin-messages">
      {/* Top bar */}
      <header className="sticky top-0 z-30 border-b border-white/10 bg-black/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-[1600px] items-center justify-between gap-4 px-6 py-4">
          <Link to="/" className="focus-red">
            <BgLogo />
          </Link>
          <div className="flex items-center gap-4">
            <span className="hidden font-mono text-[10px] uppercase tracking-[0.22em] text-brand-textDim sm:inline">
              {admin.email}
            </span>
            <button
              type="button"
              onClick={load}
              data-testid="admin-refresh"
              className="inline-flex items-center gap-2 border border-white/15 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.22em] text-white hover:bg-white/5"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${fetching ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button
              type="button"
              onClick={logout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 bg-brand-red px-3 py-2 font-mono text-[10px] uppercase tracking-[0.22em] text-white hover:bg-brand-redHover"
            >
              <LogOut className="h-3.5 w-3.5" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-[1600px] px-6 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="font-mono-tag">Admin</p>
            <h1 className="mt-3 font-heading text-3xl tracking-tight text-white sm:text-4xl">
              Contact submissions
            </h1>
            <p className="mt-2 text-sm text-brand-textDim">
              {total} total · {unread} unread
            </p>
          </div>
          <label className="flex w-full max-w-sm items-center gap-2 border border-white/15 bg-[#0A0A0A] px-3 py-2">
            <Search className="h-4 w-4 text-brand-textDim" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search…"
              className="w-full bg-transparent text-sm text-white placeholder-white/30 outline-none"
              data-testid="admin-search"
            />
          </label>
        </div>

        {err && (
          <div className="mt-6 border border-brand-red/40 bg-brand-red/10 px-3 py-2 text-sm text-brand-red">
            {err}
          </div>
        )}

        <div className="mt-8 grid gap-px border border-white/10 bg-white/5 lg:grid-cols-12">
          {/* List */}
          <div className="bg-brand-bg lg:col-span-5" data-testid="admin-messages-list">
            {fetching && messages.length === 0 ? (
              <div className="flex items-center justify-center py-20 text-brand-textDim">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
              </div>
            ) : filtered.length === 0 ? (
              <div className="px-6 py-20 text-center text-brand-textDim">
                <Mail className="mx-auto mb-3 h-6 w-6 opacity-60" />
                No messages found.
              </div>
            ) : (
              <ul className="divide-y divide-white/5">
                {filtered.map((m) => (
                  <li key={m.id}>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedId(m.id);
                        if (!m.read) markRead(m.id);
                      }}
                      data-testid={`admin-msg-${m.id}`}
                      className={`flex w-full flex-col gap-1 px-5 py-4 text-left transition-colors hover:bg-white/5 ${
                        selectedId === m.id ? "bg-white/[0.04]" : ""
                      }`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <span className="flex items-center gap-2 truncate font-heading text-sm text-white">
                          {!m.read && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-brand-red" />}
                          <span className="truncate">{m.name}</span>
                        </span>
                        <span className="shrink-0 font-mono text-[10px] tracking-[0.18em] text-brand-textDim">
                          {formatDate(m.created_at)}
                        </span>
                      </div>
                      <div className="truncate text-xs text-brand-textDim">{m.subject}</div>
                      <div className="truncate text-xs text-white/50">{m.email}</div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Detail */}
          <div className="bg-brand-bg p-6 lg:col-span-7 lg:p-10" data-testid="admin-message-detail">
            {!selected ? (
              <div className="flex h-full min-h-[40vh] flex-col items-center justify-center text-center text-brand-textDim">
                <Mail className="mb-4 h-8 w-8 opacity-60" />
                <p>Select a message to read it.</p>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="font-mono-tag">{selected.locale?.toUpperCase() || "EN"} · {formatDate(selected.created_at)}</p>
                    <h2 className="mt-3 font-heading text-2xl tracking-tight text-white sm:text-3xl">
                      {selected.subject}
                    </h2>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    {selected.read && (
                      <span className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.2em] text-emerald-400">
                        <CheckCircle2 className="h-3.5 w-3.5" /> Read
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() => remove(selected.id)}
                      data-testid="admin-delete"
                      className="inline-flex items-center gap-1 border border-white/15 px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-white/80 hover:border-brand-red hover:text-brand-red"
                    >
                      <Trash2 className="h-3.5 w-3.5" /> Delete
                    </button>
                  </div>
                </div>

                <dl className="grid grid-cols-1 gap-4 border border-white/10 p-5 sm:grid-cols-2">
                  <Row label="Name" value={selected.name} />
                  <Row label="Email" value={selected.email} href={`mailto:${selected.email}`} />
                  <Row label="Company" value={selected.company} />
                  <Row label="Country" value={selected.country} />
                  <Row label="Phone" value={selected.phone} href={selected.phone ? `tel:${selected.phone}` : null} />
                  <Row label="Locale" value={selected.locale} />
                </dl>

                <div>
                  <p className="font-mono-tag">Message</p>
                  <div className="mt-3 whitespace-pre-wrap border-l-2 border-brand-red bg-[#0A0A0A] p-5 text-sm leading-relaxed text-white/90">
                    {selected.message}
                  </div>
                </div>

                <a
                  href={`mailto:${selected.email}?subject=Re:%20${encodeURIComponent(selected.subject)}`}
                  className="inline-flex items-center gap-2 bg-brand-red px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.22em] text-white hover:bg-brand-redHover"
                  data-testid="admin-reply"
                >
                  <ExternalLink className="h-3.5 w-3.5" /> Reply via email
                </a>
              </div>
            )}
          </div>
        </div>
      </div>
    </main>
  );
}

function Row({ label, value, href }) {
  return (
    <div>
      <dt className="font-mono text-[10px] uppercase tracking-[0.22em] text-brand-textDim">{label}</dt>
      <dd className="mt-1 break-words text-sm text-white">
        {value ? (
          href ? (
            <a href={href} className="hover:text-brand-red">
              {value}
            </a>
          ) : (
            value
          )
        ) : (
          <span className="text-white/30">—</span>
        )}
      </dd>
    </div>
  );
}
